/**
 * The other locale's URL for the page currently being viewed.
 *
 * This exists because Paraglide's `setLocale()` cannot answer the question. It
 * rebuilds the URL by prefixing — `/about-us` becomes `/th/about-us` — which is
 * the single assumption `AGENTS.md` warns about most loudly: WPML translates
 * page slugs, category slugs, post slugs and four product slugs, so `/th/` plus
 * an English path is usually a URL that has never existed. The language switcher
 * was the one place that assumption had never been removed, and it 404'd on 31
 * of the 60 English pages.
 *
 * Rather than a second guess at the mapping, this reuses the pairings already
 * computed for `hreflang` — the same data the built pages have been emitting
 * correctly all along. If those are right, the switcher is right, and there is
 * only one place to fix when they are not.
 *
 * **Those pairings arrive through `page.data.alternates`, computed in each
 * route's `load`.** They used to be recomputed here by calling into
 * `$lib/seo/alternates`, which reaches the whole WooCommerce snapshot: because
 * this module is imported by the language switcher, and the switcher is in the
 * header of every page, that put ~3.5 MB of catalogue JSON (≈528 KB over the
 * wire) into the client bundle of every page on the site, purely to decide
 * where one link should point. The values are identical; only the place they
 * are computed has moved to the server, where the data already lives.
 *
 * Returning `undefined` is a real answer: `/resellers` has no Thai page and
 * `/privacy-policy` has no Thai translation, so the switcher must not offer
 * Thai there. Sending someone to a 404 is worse than not offering the link.
 */

import type { Page } from '@sveltejs/kit';
import type { SiteLocale } from '$lib/services/products';
import type { Alternate } from '$lib/seo/alternates';
import { prefixPath, withoutLocalePrefix } from '$lib/i18n';

/**
 * Routes whose paths really do differ only by the locale prefix, so prefixing is
 * the correct answer and no pairing table is needed: the shop archive, the
 * comparison table, the cart and checkout flow, and the HTML sitemap. `/` is
 * here too — the homepage is `/` in English and `/<locale>` everywhere else.
 *
 * A route added here must be added to `STATIC_PATHS` in
 * `scripts/prerender-entries.mjs` as well, or the switcher offers a locale whose
 * page was never built.
 *
 * These are the fixed SvelteKit routes. WPML did translate some of their slugs
 * (`/ja/ショップ`), but serving those would need the reroute hook to know about
 * every translated slug, so the legacy spellings 301 here instead — see
 * `scripts/lib/legacy-urls.mjs`.
 */
const PREFIXABLE = [
	'/',
	'/shop',
	'/compare',
	'/cart',
	'/checkout',
	'/checkout/complete',
	'/sitemap'
];

/**
 * Strip a leading locale prefix, segment-safely.
 *
 * Shared with the rest of the site rather than reimplemented: `/theatre` must not
 * match `/th` and `/espresso` must not match `/es`, and the first of those
 * already filed an English blog post under Thai once.
 */
const withoutLocale = withoutLocalePrefix;

const prefixed = (path: string, locale: SiteLocale) => prefixPath(path, locale);

/**
 * Alternates for whatever `page` is currently showing, or `[]` if the route
 * does not publish any — the fixed routes above, which need none.
 */
function alternatesFor(page: Page): Alternate[] {
	const alternates = (page.data as { alternates?: Alternate[] }).alternates;
	return Array.isArray(alternates) ? alternates : [];
}

/**
 * The path to switch to, or `undefined` when this page has no counterpart in
 * `locale` and the option should be hidden.
 */
export function alternatePathFor(page: Page, locale: SiteLocale): string | undefined {
	const bare = withoutLocale(page.url.pathname).replace(/\/$/, '') || '/';
	if (PREFIXABLE.includes(bare)) return prefixed(bare, locale);

	const match = alternatesFor(page).find((alternate) => alternate.locale === locale);
	return match?.path;
}
